import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { UniversalPickerModal } from 'shared/components/UniversalPickerModal';
import { useActiveUsers } from 'shared/contexts/users.context';
import { getUserName, getUserImageUrlById } from 'shared/utils/users';

interface Props {
  isOpen: boolean;
  ticketId: number;
  currentAssignedTo?: string | null;
  onClose: () => void;
  onConfirm: (assignedTo: string, message: string) => Promise<void>;
}

export function RenoAssignAndNotifyModal({
  isOpen,
  ticketId,
  currentAssignedTo,
  onClose,
  onConfirm,
}: Props) {
  const { users: activeUsers } = useActiveUsers();
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleOpen = () => {
    setAssignedTo(currentAssignedTo ?? null);
    setMessage('');
    setIsSubmitting(false);
  };

  const handleConfirm = async () => {
    if (!assignedTo) return;
    setIsSubmitting(true);
    try {
      await onConfirm(assignedTo, message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const assigneeName = assignedTo ? getUserName(assignedTo, activeUsers).name : null;
  const assigneeAvatar = assignedTo ? getUserImageUrlById(assignedTo, activeUsers) : null;

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(o) => {
          if (!o && !isSubmitting) onClose();
          if (o) handleOpen();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Doorsturen — RT-{ticketId}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs">Toewijzen aan *</Label>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="w-full flex items-center gap-2 px-3 py-2 border rounded-md text-sm hover:bg-gray-50 text-left"
              >
                {assigneeAvatar && (
                  <img src={assigneeAvatar} alt="" className="w-5 h-5 rounded-full" />
                )}
                <span className={assigneeName ? 'text-foreground' : 'text-muted-foreground'}>
                  {assigneeName ?? 'Selecteer collega...'}
                </span>
              </button>
            </div>

            <div className="space-y-1">
              <Label htmlFor="assign-message" className="text-xs">
                Bericht (optioneel)
              </Label>
              <Textarea
                id="assign-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Bijkomende context voor de collega..."
                rows={4}
                className="text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Annuleren
            </Button>
            <Button onClick={handleConfirm} disabled={!assignedTo || isSubmitting}>
              {isSubmitting ? 'Doorsturen...' : 'Doorsturen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UniversalPickerModal
        isOpen={pickerOpen}
        mode="assignee"
        title="Selecteer collega"
        onClose={() => setPickerOpen(false)}
        onConfirm={(user: any) => {
          setAssignedTo(user?.id ?? null);
          setPickerOpen(false);
        }}
      />
    </>
  );
}
